import { v4 as uuid } from 'uuid'
import { storageKey, storage } from '@lib/storage'

import {
  ApiErrorResponse,
  ApiGetResponse,
  ApiRequestType,
  ListParams,
  MetadataResponse,
  ReadParams,
} from 'types/api'
import { logger } from '@lib/logger'
import { toSearchParams } from '@utils/object'
import { isServer } from '@utils/server'
import { ApiError } from './errors'
import { withClientSide } from '@utils/client-side'
import { clearAuthToken, clearSessionUser } from '@utils/auth'

export class Api<T> {
  apiUrl: URL
  pathPrefix: string
  apiKey?: string

  constructor(
    baseUrl: string,
    { pathPrefix = '/', ...options }: { apiKey?: string; pathPrefix?: string } = {}
  ) {
    this.apiUrl = new URL(baseUrl)
    this.pathPrefix = pathPrefix
    if (options?.apiKey) this.apiKey = options.apiKey
  }

  async get<D>(path: string, query?: URLSearchParams) {
    return await this.request<D>({ path, method: 'GET', query })
  }

  async post<D, P = Partial<D>>(path: string, body: P) {
    return await this.request<D, P>({ path, method: 'POST', body })
  }

  async put<D, P = Partial<D>>(path: string, body: P) {
    return await this.request<D, P>({ path, method: 'PUT', body })
  }

  async patch<D, P = Partial<D>>(path: string, body: P) {
    return await this.request<D, P>({ path, method: 'PATCH', body })
  }

  async delete<D>(path: string, query?: URLSearchParams) {
    return await this.request<D>({ path, method: 'DELETE', query })
  }

  async list({ limit = 25, skip = 0, sort = '-created', ...params }: ListParams<T> = {}) {
    const listParams = { limit, skip, sort, ...params }
    const searchParams = toSearchParams(listParams)
    const response = await this.get<ApiGetResponse<T[]>>(this.pathPrefix, searchParams)

    return Array.isArray(response) ? { data: response } : response
  }

  async read(id: string, readParams: ReadParams = {}) {
    const searchParams = toSearchParams(readParams)
    const response = await this.get<ApiGetResponse<T>>(this.pathPrefix + `/${id}`, searchParams)

    const metadataCasted = response as MetadataResponse<T>
    const straightCasted = response as T

    return metadataCasted.data !== undefined ? metadataCasted.data : straightCasted
  }

  async create(payload: Partial<T> = {}) {
    const response = await this.post<T>(this.pathPrefix, payload)
    return response
  }

  async update(id: string, payload: Partial<T>) {
    const response = await this.put<T>(this.pathPrefix + `/${id}`, payload)
    return response
  }

  async remove(id: string, params: Record<string, string> = {}) {
    const response = await this.delete<T>(this.pathPrefix + `/${id}`, toSearchParams(params))
    return response
  }

  static getAuthToken() {
    if (isServer()) {
      return null
    }
    return storage.local.getByKey<string>(storageKey.authToken)
  }

  static getProspectToken() {
    if (isServer()) {
      return null
    }
    const token =
      storage.local.getByKey<string>(storageKey.prospectToken) ||
      storage.session.getByKey<string>(storageKey.prospectToken) ||
      uuid()
    storage.local.setByKey(storageKey.prospectToken, token)
    storage.session.setByKey(storageKey.prospectToken, token)
    return token
  }

  protected async request<D, P = Partial<D>>({
    path,
    method,
    headers,
    query,
    body,
  }: {
    path: string
    method: ApiRequestType
    headers?: Headers
    query?: URLSearchParams
    body?: P
  }): Promise<D> {
    const requestUrl = new URL(this.apiUrl)
    requestUrl.pathname = path

    const fullHeaders = new Headers(headers ?? {})
    fullHeaders.set('Accept', 'application/json')
    fullHeaders.set('Content-Type', 'application/json')
    if (this.apiKey) fullHeaders.set('api-key', this.apiKey)

    const authToken = Api.getAuthToken()
    if (authToken) {
      fullHeaders.set('x-access-token', authToken)
    }

    const prospectToken = Api.getProspectToken()
    if (prospectToken && !authToken) {
      fullHeaders.set('x-prospect-token', prospectToken)
    }

    if (query && Array.from(query.entries()).length > 0) {
      requestUrl.search = `?${query.toString()}`
    }

    const response = await fetch(requestUrl.href, {
      method,
      headers: fullHeaders,
      body: body ? JSON.stringify(body) : undefined,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
    })
    let responseJson = await response.json()

    if (response.status === 401) {
      // Add logic to logout the user on 401
      withClientSide(() => {
        clearAuthToken()
        clearSessionUser()
        window.location.reload()
      })
    }
    if (responseJson.errors) {
      const responseError = responseJson as ApiErrorResponse
      const messagePrefix = `Api.request('${requestUrl.pathname}')`
      logger.error(messagePrefix, responseError.message)
      responseError.errors.forEach((err) => logger.error(messagePrefix, err.message || err))
      throw new ApiError(response, responseError.errors?.at(-1)?.message ?? messagePrefix)
    }
    return responseJson
  }
}
